import type { IconDefinition } from '@fortawesome/pro-solid-svg-icons'
import {
  faArrowPointer,
  faCircleCheck,
  faCircleExclamation,
  faClock,
  faEnvelopeCircleCheck,
  faEnvelopeOpen,
  faTriangleExclamation
} from '@fortawesome/pro-solid-svg-icons'
import type { WithHydraPagination } from '~/api/utils'
import type {
  ApplicantHistoryEvent,
  ReceiveSmsEvent,
  SendEmailEvent,
  SendSmsEvent
} from '~/domains/actions/types'

export type EmailStatus =
  | 'bounce'
  | 'delivery'
  | 'spam_complaint'
  | 'out_of_band'
  | 'rejection'
  | 'delay'
  | 'click'
  | 'open'

export type MessageStatusTone = 'success' | 'warning' | 'danger'

export type EmailStatusConfig = {
  icon: IconDefinition
  tone: MessageStatusTone
  label: string
}

export const EMAIL_STATUS_CONFIG: Record<EmailStatus, EmailStatusConfig> = {
  delivery: {
    icon: faEnvelopeCircleCheck,
    tone: 'success',
    label: 'Distribué'
  },
  open: { icon: faEnvelopeOpen, tone: 'success', label: 'Ouvert' },
  click: {
    icon: faArrowPointer,
    tone: 'success',
    label: 'Lien cliqué'
  },
  bounce: {
    icon: faCircleExclamation,
    tone: 'danger',
    label: "Echec de l'envoi"
  },
  rejection: {
    icon: faCircleExclamation,
    tone: 'danger',
    label: 'Refusé'
  },
  spam_complaint: {
    icon: faTriangleExclamation,
    tone: 'danger',
    label: 'Signalé comme spam'
  },
  delay: { icon: faClock, tone: 'warning', label: 'En attente' },
  out_of_band: {
    icon: faCircleExclamation,
    tone: 'warning',
    label: 'Erreur externe'
  }
}

export type SmsStatus =
  | 'SCHEDULED'
  | 'ENROUTE'
  | 'DELIVERED'
  | 'UNDELIVERABLE'
  | 'UNDELIVERED'

export const SMS_STATUS_CONFIG: Record<SmsStatus, EmailStatusConfig> = {
  SCHEDULED: {
    icon: faClock,
    tone: 'warning',
    label: 'Envoi programmé'
  },
  ENROUTE: {
    icon: faClock,
    tone: 'warning',
    label: 'Envoi en cours'
  },
  DELIVERED: {
    icon: faCircleCheck,
    tone: 'success',
    label: 'Reçu'
  },
  UNDELIVERABLE: {
    icon: faCircleExclamation,
    tone: 'danger',
    label: 'Non délivrable'
  },
  UNDELIVERED: {
    icon: faTriangleExclamation,
    tone: 'danger',
    label: 'Non délivré'
  }
}

export type SmsTemplateVisibility = 'instance' | 'agency' | 'user'

export type SmsTemplateResponse = {
  id: number
  name: string
  content: string
  visibility: SmsTemplateVisibility
  forApplicant: boolean
  forContact: boolean
  agency: { id: number; name: string } | null
  user: { id: number } | null
  createdAt: string
  updatedAt: string
}

export type SmsTemplatePayload = {
  name: string
  content: string
  visibility?: SmsTemplateVisibility
  forApplicant: boolean
  forContact: boolean
  agency?: string | null
  user: { id: number } | null
}

export const isSmsStatus = (value: string): value is SmsStatus =>
  Object.hasOwn(SMS_STATUS_CONFIG, value)

export type ConversationEntityType = 'applicant' | 'contact'

export type ConversationItem = {
  type: ConversationEntityType
  id: number
  firstname: string
  lastname: string
  photoUrl: string | null
  lastActionDate: string
  lastMessageExcerpt: string | null
  isDirect: boolean
}

export type ConversationsResponse = WithHydraPagination<ConversationItem>

export type SelectedConversation = {
  type: ConversationEntityType
  id: number
}

export type MessageBubbleEvent = SendSmsEvent | ReceiveSmsEvent | SendEmailEvent

export const isMessageBubbleEvent = (
  event: ApplicantHistoryEvent
): event is MessageBubbleEvent =>
  event.type === 'sms_sent' ||
  event.type === 'sms_received' ||
  event.type === 'send_email' ||
  event.type === 'email_received'
